import type { FC } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "shared/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Textarea } from "shared/ui/textarea"

interface DialogState {
  open: boolean
  isProcessing: boolean
  stageName: string
  note: string
  error?: string | null
  noteError?: string | null
  onNoteChange: (value: string) => void
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

interface WorkflowApprovalDialogsProps {
  approveDialog: Omit<DialogState, "noteError">
  rejectDialog: DialogState
  requestReviewDialog?: DialogState
}

const DecisionDialog: FC<{
  dialog: DialogState
  title: string
  description: string
  noteLabel: string
  notePlaceholder: string
  confirmLabel: string
  processingLabel: string
  confirmClassName?: string
}> = ({
  dialog,
  title,
  description,
  noteLabel,
  notePlaceholder,
  confirmLabel,
  processingLabel,
  confirmClassName = "bg-red-600 text-white hover:bg-red-700",
}) => (
  <Dialog open={dialog.open} onOpenChange={dialog.onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 text-sm text-gray-700">
        <p>{description}</p>
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {noteLabel} <span className="text-red-500">*</span>
          </p>
          <Textarea
            value={dialog.note}
            onChange={(event) => dialog.onNoteChange(event.target.value)}
            placeholder={notePlaceholder}
            className="min-h-[120px]"
            disabled={dialog.isProcessing}
          />
          {dialog.error ? <p className="text-xs text-red-600">{dialog.error}</p> : null}
          {dialog.noteError ? <p className="text-xs text-red-600">{dialog.noteError}</p> : null}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => dialog.onOpenChange(false)}
          disabled={dialog.isProcessing}
        >
          ยกเลิก
        </Button>
        <Button onClick={dialog.onConfirm} disabled={dialog.isProcessing} className={confirmClassName}>
          {dialog.isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {processingLabel}
            </>
          ) : (
            confirmLabel
          )}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
)

export const WorkflowApprovalDialogs: FC<WorkflowApprovalDialogsProps> = ({
  approveDialog,
  rejectDialog,
  requestReviewDialog,
}) => (
  <>
    <Dialog open={approveDialog.open} onOpenChange={approveDialog.onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ยืนยันการอนุมัติ</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            คุณต้องการอนุมัติขั้นตอน{" "}
            <span className="font-semibold text-gray-900">
              {approveDialog.stageName || "ขั้นตอนนี้"}
            </span>{" "}
            หรือไม่?
          </p>
          {approveDialog.error ? <p className="text-sm text-red-600">{approveDialog.error}</p> : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => approveDialog.onOpenChange(false)}
            disabled={approveDialog.isProcessing}
          >
            ยกเลิก
          </Button>
          <Button onClick={approveDialog.onConfirm} disabled={approveDialog.isProcessing}>
            {approveDialog.isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                กำลังอนุมัติ...
              </>
            ) : (
              "ยืนยันอนุมัติ"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <DecisionDialog
      dialog={rejectDialog}
      title="ส่งกลับให้ผู้ขอแก้ไข"
      description={`ระบบจะส่งใบงานกลับให้ผู้ขอแก้ไขจากขั้นตอน ${rejectDialog.stageName || "ขั้นตอนนี้"} และผู้ขอต้องส่งอนุมัติใหม่ตาม flow เดิม`}
      noteLabel="เหตุผลในการส่งกลับ"
      notePlaceholder="กรุณาระบุเหตุผลที่ต้องการให้แก้ไข"
      confirmLabel="ยืนยันส่งกลับ"
      processingLabel="กำลังส่งกลับ..."
    />

    {requestReviewDialog ? (
      <DecisionDialog
        dialog={requestReviewDialog}
        title="ขอข้อมูลเพิ่มเติมจากผู้ขอ"
        description={`ระบบจะพาใบงานไปยังขั้นตอน REQUESTER_REVISION ต่อจาก ${requestReviewDialog.stageName || "ขั้นตอนนี้"} เพื่อให้ผู้ขอเติมข้อมูลเพิ่ม แล้วเดินต่อใน workflow เดิม`}
        noteLabel="เหตุผลที่ต้องการข้อมูลเพิ่มเติม"
        notePlaceholder="กรุณาระบุข้อมูลที่ต้องการให้ผู้ขอเพิ่มเติมหรือปรับปรุง"
        confirmLabel="ยืนยันขอข้อมูลเพิ่มเติม"
        processingLabel="กำลังส่งคำขอ..."
        confirmClassName="bg-orange-600 text-white hover:bg-orange-700"
      />
    ) : null}
  </>
)
